import {
  Box,
  CircularProgress,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material'
import {
  IconBookmark,
  IconBookmarkFilled,
  IconPointFilled,
  IconRosetteDiscountCheck,
} from '@tabler/icons-react'
import styled from 'styled-components'

export const RestaurantCardImgSectionContainer = styled(Box).attrs(() => {
  return {
    sx: {
      height: { xs: '182px', md: '200px' },
      marginBottom: '12px',
      position: 'relative',
    },
  }
})(() => ({}))

export const RestaurantCardImgSectionSkeletonContainer = styled(Skeleton).attrs(
  () => {
    return {
      variant: 'rectangular',
      sx: {
        borderRadius: '20px',
        height: { xs: 182, md: 200 },
      },
      width: '100%',
    }
  },
)(() => ({}))

export const RestaurantCardImgSectionTagChipContainer = styled(Box).attrs(
  () => {
    return {
      sx: {
        position: 'absolute',
        top: '6px',
        left: '6px',
        zIndex: 100,
      },
    }
  },
)(() => ({}))

export const RestaurantCardImgSectionIconBookmarkFilledContainer = styled(
  Box,
).attrs(() => {
  return {
    sx: {
      position: 'absolute',
      top: '7px',
      right: '7px',
      zIndex: 100,
      cursor: 'pointer',
    },
  }
})(() => ({}))

export const RestaurantCardImgSectionCircularProgress = styled(
  CircularProgress,
).attrs(() => {
  const theme = useTheme()
  return {
    color: 'inherit',
    style: {
      width: '30px',
      height: '30px',
      color: theme.palette.background.paper,
    },
  }
})(() => ({}))

export const RestaurantCardImgSectionIconBookmarkFilled = styled(
  IconBookmarkFilled,
).attrs(() => {
  const theme = useTheme()
  return {
    color: theme.palette.background.paper,
    height: 20,
    width: 20,
  }
})(() => ({}))

export const RestaurantCardImgSectionIconBookmark = styled(IconBookmark).attrs(
  () => {
    return {
      color: 'white',
      height: 20,
      width: 20,
      stroke: 2,
    }
  },
)(() => ({}))

export const RestaurantCardInfoContainer = styled(Box).attrs(() => {
  return {
    sx: {
      px: '4px',
    },
  }
})(() => ({}))

export const RestaurantCardInfoTitleContainer = styled(Box).attrs(() => {
  return {
    sx: {
      display: 'flex',
      alignItems: 'center',
      textAlign: 'center',
    },
  }
})(() => ({}))

export const RestaurantCardInfoTitleTypography = styled(Typography).attrs(
  () => {
    return {
      noWrap: true,
      variant: 'body2',
      component: 'span',
      sx: {
        textTransform: 'capitalize',
        textAlign: 'left',
        mr: '5px',
        mb: '4px',
      },
      maxWidth: '100%',
    }
  },
)(() => ({}))

export const RestaurantCardInfoTitleIconRosetteDiscountCheck = styled(
  IconRosetteDiscountCheck,
).attrs(() => {
  const theme = useTheme()
  return {
    stroke: 2,
    size: 20,
    color: theme.palette.success.dark,
  }
})(() => ({}))

export const RestaurantCardInfoDescriptionContainer = styled(Box).attrs(() => {
  return {
    sx: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
    },
  }
})(() => ({}))

export const RestaurantCardInfoDescriptionTypography = styled(Typography).attrs(
  () => {
    const theme = useTheme()

    return {
      noWrap: true,
      variant: 'subtitle1',
      component: 'span',
      sx: {
        textOverflow: 'ellipsis',
        maxWidth: { xs: '65%', md: '70%' },
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textTransform: 'capitalize',
        color: theme.palette.grey[400],
        flexShrink: 1,
        flexGrow: 1,
      },
    }
  },
)(() => ({}))

export const RestaurantCardInfoViewMenuTypography = styled(Typography).attrs(
  () => {
    const theme = useTheme()

    return {
      variant: 'subtitle1',
      component: 'span',
      sx: {
        color: theme.palette.primary.main,
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      },
    }
  },
)(() => ({}))

export const RestaurantCardInfoDescriptionIconPointFilled = styled(
  IconPointFilled,
).attrs(() => {
  return {
    height: 17,
    width: 17,
  }
})(() => {
  const theme = useTheme()
  return {
    marginLeft: '8px',
    marginRight: '8px',
    color: theme.palette.grey[400],
  }
})

export const RestaurantCardInfoDescriptionDistanceTypography = styled(
  Typography,
).attrs(() => {
  const theme = useTheme()

  return {
    variant: 'subtitle1',
    component: 'span',
    sx: {
      textTransform: 'capitalize',
      color: theme.palette.grey[400],
      display: 'flex',
      alignItems: 'center',
      textAlign: 'left',
    },
  }
})(() => ({}))

export const RestaurantCardActionBarContainer = styled(Box).attrs(() => {
  return {
    className: 'restaurant-card-action-bar',
    sx: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
      mt: '6px',
      px: '2px',
    },
  }
})(() => ({}))

// LEFT side of the action row: the "View menu →" tap-in affordance + distance. Clicks here are NOT
// isolated — they bubble to the surface wrapper so the card still navigates to the detail page.
export const RestaurantCardActionBarLeft = styled(Box).attrs(() => {
  return {
    sx: {
      display: 'flex',
      alignItems: 'center',
      minWidth: 0,
      overflow: 'hidden',
      cursor: 'pointer',
    },
  }
})(() => ({}))

// RIGHT side of the action row: the intent actions (Directions / Call / Share). This container
// stops click + mousedown propagation so taps on the buttons (or the gaps between them) never
// trigger the surface wrapper's navigate-to-detail.
export const RestaurantCardActionBarActions = styled(Box).attrs(() => {
  return {
    sx: {
      display: 'flex',
      alignItems: 'center',
      gap: '2px',
      flexShrink: 0,
    },
  }
})(() => ({}))

export const RestaurantCardMatchQualityContainer = styled(Box).attrs(() => {
  return {
    sx: {
      width: '100%',
      mt: '6px',
    },
  }
})(() => ({}))

export const RestaurantCardMatchQualityTypography = styled(Typography).attrs(
  () => {
    const theme = useTheme()

    return {
      variant: 'caption',
      component: 'span',
      sx: {
        textTransform: 'capitalize',
        color: theme.palette.grey[500],
        display: 'block',
        textAlign: 'left',
        fontSize: '12px',
        fontWeight: 500,
      },
    }
  },
)(() => ({}))
